// Toast component using Radix UI Toast primitives
// Shared across entire app (admin, engineer, etc.)
//
// Two looks (DEV-51010):
// - `standard` (admin, desktop): tinted card top-right, as before.
// - `engineer` (mobile): a bar at the bottom, above the nav and any sticky
//   action bar (see utils/heightVarRef `toastClearanceRef`), where "Undo" and
//   "Add to list" are in thumb reach. At the top they covered the back
//   button. Confirmations use the nav colours so a routine save doesn't look
//   like an alert; errors and warnings keep their light tint.
import { createContext, useContext, useState, useCallback } from 'react'
import * as RadixToast from '@radix-ui/react-toast'
import { CheckCircle, XCircle, Info, AlertTriangle, AlertCircle, X } from 'lucide-react'
import clsx from 'clsx'

export type ToastType = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number
  action?: {
    label: string
    onClick: () => void
  }
  // Bold heading above the message, for toasts that need more than one line
  // of explanation (e.g. "Postcode updated").
  title?: string
}

interface ToastContextValue {
  addToast: (
    message: string,
    type: ToastType,
    duration?: number,
    action?: Toast['action'],
    title?: string,
  ) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast() {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within ToastProvider')
  }
  return context
}

const typeStyles: Record<ToastType, { bg: string; border: string; text: string; icon: string }> = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
    icon: 'text-green-500',
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
    icon: 'text-red-500',
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
    icon: 'text-blue-500',
  },
  warning: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
    icon: 'text-amber-500',
  },
}

const typeIcons: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5" />,
  error: <XCircle className="w-5 h-5" />,
  info: <Info className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
}

export type ToastVariant = 'standard' | 'engineer'

// Engineer toasts: nav colours for confirmations, light tint for problems.
const engineerTypeStyles: Record<ToastType, string> = {
  success: 'bg-nav text-nav border-transparent',
  info: 'bg-nav text-nav border-transparent',
  error: 'bg-red-50 border-red-200 text-gray-900',
  warning: 'bg-amber-50 border-amber-200 text-gray-900',
}

const engineerTypeIcons: Partial<Record<ToastType, React.ReactNode>> = {
  error: <AlertCircle className="h-5 w-5 flex-shrink-0 text-red-600" aria-hidden="true" />,
  warning: <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />,
}

// An engineer toast's action ("Undo", "Add to list", "Manage order"): a 44px
// tap target in the brand colour. `className` places it in the layout.
function EngineerToastAction({
  action,
  className,
}: {
  action: NonNullable<Toast['action']>
  className: string
}) {
  return (
    <RadixToast.Action
      altText={action.label}
      onClick={action.onClick}
      className={clsx(
        'inline-flex h-11 shrink-0 items-center justify-center rounded-lg px-3 text-sm font-bold text-brand-primary transition-colors hover:bg-nav-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
        className,
      )}
    >
      {action.label}
    </RadixToast.Action>
  )
}

let toastIdCounter = 0

export function ToastProvider({
  children,
  variant = 'standard',
}: {
  children: React.ReactNode
  variant?: ToastVariant
}) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const addToast = useCallback(
    (
      message: string,
      type: ToastType,
      duration = 3000,
      action?: Toast['action'],
      title?: string,
    ) => {
      const id = String(++toastIdCounter)
      const toast: Toast = { id, message, type, duration, action, title }
      setToasts((prev) => [...prev, toast])

      // Auto-remove toast after duration (fallback in case Radix doesn't auto-dismiss)
      if (duration && duration !== Infinity) {
        setTimeout(() => {
          removeToast(id)
        }, duration)
      }
    },
    [removeToast]
  )

  return (
    <ToastContext.Provider value={{ addToast }}>
      <RadixToast.Provider swipeDirection="right" duration={3000}>
        {children}

        {variant === 'engineer' && toasts.map((toast) => (
          <RadixToast.Root
            key={toast.id}
            duration={toast.duration ?? 3000}
            open={true}
            onOpenChange={(open) => {
              if (!open) removeToast(toast.id)
            }}
            className={clsx(
              // Closed toasts unmount at once, so the enter animation needs no
              // data-state variant (reduced motion is handled in index.css).
              'animate-toast-slide-up pointer-events-auto relative flex min-h-14 w-full items-center gap-3 rounded-xl border py-2 pl-4 pr-11 shadow-lg',
              engineerTypeStyles[toast.type],
            )}
          >
            {engineerTypeIcons[toast.type]}
            {toast.title ? (
              // A titled toast has more text than fits beside a button on a
              // phone, so it stacks: title, message, then the action.
              <div className="min-w-0 flex-1 py-1">
                <RadixToast.Title className="text-sm font-bold leading-snug">
                  {toast.title}
                </RadixToast.Title>
                <RadixToast.Description className="mt-0.5 text-sm leading-snug">
                  {toast.message}
                </RadixToast.Description>
                {toast.action && (
                  <EngineerToastAction action={toast.action} className="-mb-1 -ml-3 mt-1" />
                )}
              </div>
            ) : (
              <>
                <RadixToast.Description className="min-w-0 flex-1 text-sm font-semibold leading-snug">
                  {toast.message}
                </RadixToast.Description>
                {toast.action && <EngineerToastAction action={toast.action} className="-my-1" />}
              </>
            )}
            <RadixToast.Close
              className={clsx(
                'absolute right-1 flex h-11 w-10 items-center justify-center rounded-lg opacity-70 transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary',
                // Beside the title on a stacked toast; centred on a one-liner.
                toast.title ? 'top-1' : 'top-1/2 -translate-y-1/2',
              )}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </RadixToast.Close>
          </RadixToast.Root>
        ))}

        {variant === 'standard' && toasts.map((toast) => {
          const style = typeStyles[toast.type]
          return (
            <RadixToast.Root
              key={toast.id}
              duration={toast.duration ?? 3000}
              open={true}
              onOpenChange={(open) => {
                if (!open) removeToast(toast.id)
              }}
              className={clsx(
                'fixed top-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50',
                'data-[state=open]:animate-slide-in-right data-[state=closed]:animate-slide-out-right',
                style.bg,
                style.border,
                'border-2 rounded-xl p-4 shadow-lg'
              )}
            >
              <div className="flex items-start gap-3">
                <span className={style.icon}>{typeIcons[toast.type]}</span>

                <div className="flex-1 min-w-0">
                  {toast.title && (
                    <RadixToast.Title className={clsx('text-sm font-semibold', style.text)}>
                      {toast.title}
                    </RadixToast.Title>
                  )}
                  <RadixToast.Description className={clsx('text-sm font-medium', style.text)}>
                    {toast.message}
                  </RadixToast.Description>

                  {toast.action && (
                    <RadixToast.Action
                      altText={toast.action.label}
                      onClick={toast.action.onClick}
                      className={clsx(
                        'text-sm font-semibold underline mt-2 hover:opacity-80',
                        style.text
                      )}
                    >
                      {toast.action.label}
                    </RadixToast.Action>
                  )}
                </div>

                <RadixToast.Close
                  className={clsx(
                    'flex-shrink-0 hover:opacity-70 transition-opacity',
                    style.icon
                  )}
                  aria-label="Close"
                >
                  <X className="h-4 w-4" />
                </RadixToast.Close>
              </div>
            </RadixToast.Root>
          )
        })}

        <RadixToast.Viewport
          className={
            variant === 'engineer'
              ? // 4rem = the bottom nav. Stacks upwards; full width on a phone.
                'fixed inset-x-0 bottom-[calc(4rem+var(--toast-clearance,0px))] z-[100] flex max-h-screen flex-col gap-2 p-3 outline-none sm:left-auto sm:right-4 sm:w-[420px]'
              : undefined
          }
        />
      </RadixToast.Provider>
    </ToastContext.Provider>
  )
}
